/**
 * The push api (docs/NESTS.md, "Stage 3: notifications"), with this device's id in
 * `X-Device-Id` as the nest calls do. The subscription's endpoint and keys go to the api
 * and nowhere else: never into analytics, a log or local storage.
 */
import { API_URL } from '../env.ts';
import { getDeviceId } from '../platform/identity.ts';
import { DEFAULT_PREFS, type Played, type PushPrefs, readPrefs } from './state.ts';

export interface PushConfig {
	enabled: boolean;
	publicKey: string | null;
}

export interface PushStatus {
	timeZone: string;
	prefs: PushPrefs;
}

export interface PushTransport {
	fetch: typeof fetch;
	deviceId: () => string;
	apiUrl: string;
}

const defaultTransport = (): PushTransport => ({ fetch: (...a) => fetch(...a), deviceId: () => getDeviceId(), apiUrl: API_URL });
let transport: PushTransport | null = null;
const t = (): PushTransport => (transport ??= defaultTransport());

/** Tests: swap the transport (null: back to the real one). */
export function setPushTransport(next: PushTransport | null): void {
	transport = next;
}

export class PushApiError extends Error {
	constructor(readonly status: number) {
		super(`push api ${status}`);
	}
}

async function call(method: 'GET' | 'POST', path: string, body?: unknown, signal?: AbortSignal): Promise<unknown> {
	const tr = t();
	const headers: Record<string, string> = { Accept: 'application/json', 'X-Device-Id': tr.deviceId() };
	if (body !== undefined) headers['Content-Type'] = 'application/json';
	const res = await tr.fetch(`${tr.apiUrl}/v1/push${path}`, {
		method,
		headers,
		cache: 'no-store',
		...(body !== undefined ? { body: JSON.stringify(body) } : {}),
		...(signal ? { signal } : {}),
	});
	if (res.status === 204) return null;
	if (!res.ok) throw new PushApiError(res.status);
	return (await res.json().catch(() => null)) as unknown;
}

const field = (o: unknown, k: string): unknown => (typeof o === 'object' && o !== null ? (o as Record<string, unknown>)[k] : undefined);

function toStatus(raw: unknown): PushStatus {
	const prefs = readPrefs(field(raw, 'prefs'), DEFAULT_PREFS);
	const tz = field(raw, 'timeZone');
	return { timeZone: typeof tz === 'string' ? tz : '', prefs };
}

let config: Promise<PushConfig> | null = null;

/** `GET /v1/push`, once per page load (a failure is "off" and is asked again next time). */
export function fetchPushConfig(): Promise<PushConfig> {
	config ??= call('GET', '')
		.then((raw) => {
			const key = field(raw, 'publicKey');
			return { enabled: field(raw, 'enabled') === true && typeof key === 'string', publicKey: typeof key === 'string' ? key : null };
		})
		.catch(() => {
			config = null;
			return { enabled: false, publicKey: null };
		});
	return config;
}

/** Tests: forget the cached config. */
export function resetPushConfig(): void {
	config = null;
}

/** The subscription as the api takes it: the endpoint and keys only. */
export function subscriptionBody(sub: PushSubscription): { endpoint: string; keys: { p256dh: string; auth: string } } | null {
	const json = sub.toJSON();
	const p256dh = json.keys?.p256dh;
	const auth = json.keys?.auth;
	if (!json.endpoint || !p256dh || !auth) return null;
	return { endpoint: json.endpoint, keys: { p256dh, auth } };
}

/**
 * `played` (contract, "Play state"): the last daily finished on its day and the streak, so
 * a reminder never arrives after you've played. Sent whatever "Count my plays" says.
 */
export async function postSubscription(sub: PushSubscription, timeZone: string, prefs: Partial<PushPrefs>, played?: Played | null): Promise<PushStatus> {
	const subscription = subscriptionBody(sub);
	if (!subscription) throw new PushApiError(0);
	return toStatus(await call('POST', '/subscription', { subscription, timeZone, prefs, ...(played ? { played } : {}) }));
}

export async function postPreferences(patch: { timeZone?: string; prefs?: Partial<PushPrefs>; played?: Played | null }): Promise<PushStatus> {
	const { played, ...rest } = patch;
	return toStatus(await call('POST', '/preferences', { ...rest, ...(played ? { played } : {}) }));
}

/** `POST /v1/push/test`: "Notifications are on", now. 404 without a subscription, 429 past 3 an hour. */
export async function postTest(): Promise<void> {
	await call('POST', '/test');
}

export async function postUnsubscribe(): Promise<void> {
	await call('POST', '/unsubscribe');
}
